import React, { useCallback, useEffect, useState } from 'react';
import { TabGroup, Tab } from '../TabGroup/TabGroup';
import { css } from '@emotion/css';
import UserWithoutSubscription from '../UserWithoutSubscription';
import UserWithNoActiveOrders from '../UserWithNoActiveOrders';
import { useAuth } from '../../context/auth-context-aem';
import getUpcomingOrder from '../../service/upcoming-order-service';
import { useApp } from '../../context/app-context';
import UserWithActiveOrders from '../UserWithActiveOrders/UserWithActiveOrders';
import { NotificationToastsList } from '../NotificationToast/NotificationToast';
import getFutureOrders from '../../service/future-orders-service';
import SecondTabContent from './SecondTabContent';
import Loader from '../../common/Loading/loader';
import Footer from './Footer';
import { ItemsContext } from '../ScheduledItems/ItemsContext';
import { get, has, isEmpty } from 'lodash';
import editQuantity from '../../service/edit-quantity-service';
import formatString from '../../utils/formatString';
import appLabels from '../../utils/appLabels';
import getSlotsInfo from '../../service/get-slots-service';
import ThirdTabContent from './ThirdTabContent';
import UserMessage from '../../common/UserMessage/UserMessage';
import AppLabels from '../../utils/appLabels';
import { PreferencesContext } from '../Preferences/PreferencesContext';
import updateNextOrderSlots from '../../service/update-next-order-slots-service';
import getAllAvailableSlotsInfo, { AvailableSlotsPayload } from '../../service/get-all-available-slots';
import { SessionContext } from './SessioncContext';
import { cacheFn } from '../../utils/cacheFn';
import getAllPreferences from '../../service/all-preferences-service';
import skipItem from '../../service/skip-item-service';
import { TooltipContainer } from '../../common/PopOver/PopOver';
import moment from 'moment/moment';
import { otaManualBootstrapConfiguration } from '../../utils/constants';

const titleStyle = css`
  font-weight: 600;
  font-size: 1.75rem;
  line-height: 2.125rem;
  color: #2a2928;
`;

const landingPageStyle = css`
  min-height: 50vh;

  @media (max-width: 641px) {
    position: initial;
    min-height: 90vh;
  }
`;

const bannerStyle = css`
  margin-top: 1rem;
`;

const ariaLiveRegionStyle = css`
  position: absolute;
  left: -10px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
`;

export function LandingPageWeb(): JSX.Element {
  // Contexts variables
  const currentAuthContext = useAuth();
  const user = get(currentAuthContext, 'user');
  const checkUserHasActiveSession = get(currentAuthContext, 'checkUserHasActiveSession');

  // State variables
  const [upcomingOrders, setUpcomingOrders] = useState({});
  const [preferencesData, setPreferencesData] = useState({});
  const [prevUpcomingOrders, setPrevUpcomingOrders] = useState({});
  const [futureOrders, setFutureOrders] = useState({});
  const [slotsInfo, setSlotsInfo] = useState({});
  const [isUserWithoutSubscription, setIsUserWithoutSubscription] = useState<boolean>(false);
  const [isUserWithNoActiveOrder, setIsUserWithNoActiveOrder] = useState(false);
  const [hasRestrictedItems, setHasRestrictedItems] = useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const [isFutureApiError, setIsFutureApiError] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(true);
  const [isFutureApiLoading, setIsFutureApiLoading] = useState(true);
  const [otaToSnSFrequencyOptions, setOtaToSnSFrequencyOptions] = useState<object[]>([]);
  const paymentFailure = get(upcomingOrders, 'preCheckFailReason') === 'PAYMENT_FAILURE';
  const userOnHold = get(upcomingOrders, 'userStatus') === 'HOLD';
  const fraudUser = get(upcomingOrders, 'userStatus') === 'FRAUD';

  const {
    showToast,
    appData,
    globalBannerMessage,
    setGlobalBannerMessage,
    setFirstTabBannerMessage,
    globalAriaLiveMessage,
  } = useApp();

  useEffect(() => {
    if (user && user.authToken) {
      console.log(new Date().toISOString(), 'Desktop: Upcoming orders request userID', get(user, 'UUID'));
      getUpcomingOrders();

      getPreferences();

      initializeSnsOneTimeModal();
    }
  }, [user]);

  const initializeSnsOneTimeModal = () => {
    try {
      if (window['appConfigServiceRef'] && window['appConfigServiceRef']['service']) {
        window['appConfigServiceRef']['service'].initManualBootstrap(otaManualBootstrapConfiguration);
      } else {
        console.error('appConfigServiceRef or service is not defined');
      }
    } catch (error) {
      console.error('Failed to initialize PDP, CouponList, Offer Detail Components:', error);
    }
  };

  useEffect(() => {
    if (upcomingOrders) {
      if (!get(upcomingOrders, 'isNewUser') && get(upcomingOrders, 'userWithNoActiveSubscriptions')) {
        setIsUserWithNoActiveOrder(true);
      } else {
        setIsUserWithNoActiveOrder(false);
        setHasRestrictedItems(get(upcomingOrders, 'hasRestrictedItems', false));
      }

      if (
        has(upcomingOrders, 'isNewUser') &&
        get(upcomingOrders, 'isNewUser', '') !== get(prevUpcomingOrders, 'isNewUser', '')
      ) {
        if (get(upcomingOrders, 'isNewUser')) {
          setIsUserWithoutSubscription(true);
        } else {
          setIsUserWithoutSubscription(false);

          if (!get(upcomingOrders, 'userWithNoActiveSubscriptions')) {
            getFutureOrderData();
          }
        }
      }

      setPrevUpcomingOrders(upcomingOrders);
    }
  }, [upcomingOrders]);

  useEffect(() => {
    if (Object.keys(upcomingOrders).length) {
      analyticsEvents('upcoming-orders', upcomingOrders);
    } else {
      analyticsPageloadCheck(true);
    }
  }, [upcomingOrders]);

  useEffect(() => {
    const paymentFailure = get(upcomingOrders, 'preCheckFailReason') === 'PAYMENT_FAILURE';
    const orderStatus: string = `${get(upcomingOrders, 'upcomingOrder.orderStatus')}`;

    if (orderStatus === 'HOLD') {
      setGlobalBannerMessage(AppLabels.PreferenceTab.userOnHoldMessage);
    }

    if (fraudUser) {
      setGlobalBannerMessage(AppLabels.PreferenceTab.fraudBannerMessage);
    }

    if (/(failed)/i.test(orderStatus) && !paymentFailure) {
      setFirstTabBannerMessage(appLabels.UserWithActiveOrders.failedOrderBannerMessage);
    }
  }, [upcomingOrders, fraudUser]);

  useEffect(() => {
    const isPaymentDetailsNotAvailable = !get(preferencesData, 'paymentsDetailsAvailable', false);
    const precheckFailedReason = get(preferencesData, 'preCheckFailedReason');

    if (
      isPaymentDetailsNotAvailable &&
      (precheckFailedReason === null || precheckFailedReason !== 'PAYMENT_FAILURE') &&
      fraudUser === false &&
      !isEmpty(upcomingOrders) &&
      !isEmpty(preferencesData)
    ) {
      setGlobalBannerMessage(AppLabels.PreferenceTab.noPaymentMethodBanner);
    } else if (globalBannerMessage === AppLabels.PreferenceTab.noPaymentMethodBanner && fraudUser !== true) {
      // only hide the banner if it was the same message
      setGlobalBannerMessage('');
    }
  }, [preferencesData, upcomingOrders, globalBannerMessage, fraudUser]);

  const analyticsEvents = (page, upcomingOrders) => {
    try {
      if (window['AB']?.DATALAYER && window?.['digitalData']?.pageFlags?.SNSInitPageload) {
        window['AB'].DATALAYER.SNSInit(page, upcomingOrders);
      }
      analyticsPageloadCheck(false);
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const analyticsPageloadCheck = (truthVal) => {
    try {
      if (window?.['digitalData']) {
        window['digitalData'].pageFlags.SNSInitPageload = truthVal;
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const ensureActiveSession = async (callback) => {
    // no need to check session on web
    return callback();
  };

  const fetchUpcomingOrders = useCallback(
    cacheFn(async (): Promise<void> => {
      const upcomingOrder = await ensureActiveSession(() => getUpcomingOrder(appData.environmentVariables, user));
      setUpcomingOrders(get(upcomingOrder, 'data', {}));
      setOtaToSnSFrequencyOptions(get(upcomingOrder, 'data.frequencies', []));
    }, 1),
    [],
  );

  const getUpcomingOrders = async (): Promise<void> => {
    setIsApiLoading(true);
    try {
      await fetchUpcomingOrders();
    } catch (error) {
      setIsApiError(true);
    } finally {
      setIsApiLoading(false);
    }
  };

  const fetchFutureOrderData = useCallback(
    cacheFn(async (): Promise<void> => {
      console.log(new Date().toISOString(), 'Desktop: Future orders request userID', get(user, 'UUID'));
      const futureOrdersResp = await ensureActiveSession(() => getFutureOrders(appData.environmentVariables, user));
      setFutureOrders(futureOrdersResp.data);
    }, 1),
    [],
  );

  const getFutureOrderData = async (): Promise<void> => {
    setIsFutureApiLoading(true);
    try {
      await fetchFutureOrderData();
    } catch (error) {
      setIsFutureApiError(true);
    } finally {
      setIsFutureApiLoading(false);
    }
  };

  const getPreferences = useCallback(
    cacheFn(async (): Promise<any> => {
      const resp = await ensureActiveSession(async () => {
        return get(await getAllPreferences(appData.environmentVariables, user), 'data');
      });

      setPreferencesData(resp);
      return resp;
    }, 100),
    [],
  );

  const fetchSlotsInfo = async (): Promise<void> => {
    setSlotsInfo({ loading: true });

    try {
      const storeID = get(upcomingOrders, 'storeId', '');

      if (!isEmpty(storeID)) {
        const resp = await ensureActiveSession(() => getSlotsInfo(appData.environmentVariables, user, storeID));
        setSlotsInfo({ data: resp.data, loading: false });
      } else {
        setSlotsInfo({ error: true, loading: false });
      }
    } catch (err) {
      setSlotsInfo({ error: err, loading: false });
    }
  };

  const updateSlotsInfo = async (slotsInfo: any): Promise<any> => {
    const payload = {
      customerId: get(user, 'UUID', ''),
      storeId: get(upcomingOrders, 'storeId', ''),
      guid: get(user, 'customerId', ''),
      nextOrderID: get(upcomingOrders, 'upcomingOrder.snsNextOrderId'),
      transactionId: get(user, 'UUID', ''),
      itemCount: get(upcomingOrders, 'upcomingOrder.numberOfItems'),
      uniqueItemCount: get(upcomingOrders, 'upcomingOrder.orderItems.length'),
      fulfillment: {
        fulfillmentType: get(upcomingOrders, 'fulfillment.fulfillmentType'),
        slot: slotsInfo,
      },
    };

    try {
      const resp = await ensureActiveSession(() => updateNextOrderSlots(appData.environmentVariables, user, payload));

      if (get(resp, 'status') === 200) {
        showToast(
          'info',
          get(
            resp,
            'message',
            formatString(appLabels.SlotPreferences.SLOTS_SUCCESS, {
              startTime: moment(get(slotsInfo, 'slotStartTS'), 'YYYY-MM-DDThh:mm:ss').format('h A'),
              deliveryDate: moment(get(slotsInfo, 'slotStartTS'), 'YYYY-MM-DDThh:mm:ss').format('MMM. D'),
              endTime: moment(get(slotsInfo, 'slotEndTS'), 'YYYY-MM-DDThh:mm:ss').format('h A'),
              deliveryType: get(upcomingOrders, 'fulfillment.fulfillmentType') === 'DUG' ? 'pickup' : 'delivery',
              date: moment(get(upcomingOrders, 'upcomingOrder.orderDate'), 'YYYY-M-D').format('MMM. D'),
            }),
          ),
        );
        return resp;
      } else {
        showToast('error', appLabels.SlotPreferences.AVAILABLE_SLOT_SAVE_ERROR);
        return { error: true };
      }
    } catch (err) {
      showToast('error', appLabels.SlotPreferences.AVAILABLE_SLOT_SAVE_ERROR);
      return { error: true };
    }
  };

  const fetchAvailableSlotsInfo = async (payload: AvailableSlotsPayload) => {
    return ensureActiveSession(() => getAllAvailableSlotsInfo(appData.environmentVariables, user, payload));
  };

  const ItemUpdateQuantityFn = async (props) => {
    analyticsPageloadCheck(false);
    const q = get(props, 'quantity');
    const maxQuantity = get(props, 'maxQuantity');
    const newQuantity = [1, maxQuantity, +q].sort((a, b) => a - b)[1];

    const resp = await ensureActiveSession(() =>
      editQuantity(appData.environmentVariables, user, {
        nextOrderId: get(props, 'nextOrderId'),
        nextOrderItemId: get(props, 'nextOrderItemId'),
        subscriptionId: get(props, 'subscriptionId'),
        isReplinishItem: get(props, 'isReplinishItem'),
        itemBpn: get(props, 'itemBpn'),
        cartId: get(props, 'cartId'),
        quantity: newQuantity,
      }),
    );

    showToast(
      'info',
      formatString(appLabels.QuantitySelector.updatedText, {
        itemDescription: get(props, 'item.itemDescription'),
      }),
    );

    if (get(resp, 'data.statusCode') === 200) {
      await fetchUpcomingOrders();
    }
  };

  const skipItemOnce = async (itemInfo) => {
    analyticsPageloadCheck(false);
    await ensureActiveSession(() => skipItem(appData.environmentVariables, user, itemInfo));
  };

  if (!user) {
    return (
      <div className={landingPageStyle}>
        <Loader />
      </div>
    );
  }

  return (
    <SessionContext.Provider
      value={{
        user,
        checkUserHasActiveSession,
        ensureActiveSession,
      }}
    >
      <div className={landingPageStyle}>
        {isUserWithoutSubscription ? (
          <UserWithoutSubscription />
        ) : (
          <div>
            <NotificationToastsList />
            <TooltipContainer />
            <div aria-live="polite" className={ariaLiveRegionStyle}>
              {globalAriaLiveMessage}
            </div>
            <div className={titleStyle}>Manage Schedule & Save</div>

            {!isEmpty(globalBannerMessage) ? (
              <UserMessage className={bannerStyle} messageText={globalBannerMessage} messageType="warning" />
            ) : null}

            <TabGroup isLoading={isApiLoading} paymentFailure={paymentFailure}>
              <Tab header="Upcoming orders" disabled={(!isApiLoading && userOnHold) || fraudUser}>
                <PreferencesContext.Provider
                  value={{
                    fetchSlotsInfo,
                    slotsInfo,
                    updateSlotsInfo,
                    fetchAvailableSlotsInfo,
                  }}
                >
                  <ItemsContext.Provider
                    value={{
                      updateQuantityFn: ItemUpdateQuantityFn,
                      fetchItemsInfo: async () => {
                        if (has(upcomingOrders, 'isNewUser')) {
                          if (get(upcomingOrders, 'isNewUser')) {
                            fetchUpcomingOrders();
                          } else {
                            fetchUpcomingOrders();
                            fetchFutureOrderData();
                          }
                        }
                      },
                      fetchUpcomingOrdersInfo: async () => {
                        fetchUpcomingOrders();
                      },
                      skipItemOnce,
                      otaToSnsFrequencyOptions: otaToSnSFrequencyOptions,
                    }}
                  >
                    {isUserWithNoActiveOrder ? (
                      <UserWithNoActiveOrders />
                    ) : (
                      // first tab content for user with active orders
                      <UserWithActiveOrders
                        upcomingOrders={upcomingOrders}
                        futureOrders={futureOrders}
                        isApiError={isApiError}
                        isFutureApiError={isFutureApiError}
                        isApiLoading={isApiLoading}
                        isFutureApiLoading={isFutureApiLoading}
                        hasRestrictedItems={hasRestrictedItems}
                        preferencesData={preferencesData}
                      />
                    )}
                  </ItemsContext.Provider>
                </PreferencesContext.Provider>
              </Tab>
              <Tab header="Scheduled items" disabled={(!isApiLoading && userOnHold) || fraudUser}>
                <SecondTabContent upcomingOrders={upcomingOrders} />
              </Tab>
              <Tab header="Preferences">
                <ThirdTabContent
                  getPreferences={() => getPreferences()}
                  getUpcomingOrders={() => fetchUpcomingOrders()}
                  preferencesData={preferencesData}
                  upcomingOrders={upcomingOrders}
                />
              </Tab>
            </TabGroup>
          </div>
        )}

        {!isUserWithoutSubscription ? <Footer analyticsState={isUserWithNoActiveOrder} /> : null}
      </div>
    </SessionContext.Provider>
  );
}

export default LandingPageWeb;
